/**
 * R-15 D2 (T-111) — the shell's side of the feedback pill: whether the module is installed (the feedback_item type is in
 * the schema), and the one form every pill opens — Report a problem / Give feedback / Ask for help — with the context it
 * will send shown read-only, an opt-in screenshot, and POST /api/feedback (L2's intake route; anyone signed in may report).
 */
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { apiUpload, newCorrelationId } from '@/api/client';
import type { Me } from '@/api/types';
import { useExperience } from '@/app/experience/ExperienceProvider';
import { useServingVersion } from '@/features/apps/AppAboutPage';
import { useAllEntityTypes } from '@/features/registry/entity-types';
import { Button, FEEDBACK_KINDS, type FeedbackKind, FeedbackProvider, Field, FieldColumn, Input, Modal, Textarea } from '@/kit';
import type { FeedbackValue, PillOrigin } from '@/kit/feedback';
import { useApps } from '@/features/apps/apps-api';
import { aboutLine, contextRows, feedbackBody, feedbackContext, screenIdFor } from './feedback-model';

const FILE_ITEM = 'file' as const;
const ALREADY_ATTACHED = 'A screenshot is already attached — remove it to paste another';
const PLAIN_TEXT = 'text/plain';

/**
 * A paste is a screenshot only when the clipboard carries an image and no text: Excel, Word and PowerPoint put an image
 * rendering beside text/plain, and that is a text paste. A browser's "Copy image" writes text/html (an <img> tag) beside
 * the image but no text/plain, so it is a screenshot. `plainText` is the clipboard's text/plain; left out, the presence of
 * a text/plain item stands for it.
 */
export function pastedScreenshot(items: { kind: string; type: string; getAsFile: () => File | null }[], plainText?: string): File | null {
  const hasText = plainText === undefined ? items.some((i) => i.type === PLAIN_TEXT) : Boolean(plainText.trim());
  if (hasText) return null;
  return items.find((i) => i.kind === FILE_ITEM && i.type.startsWith('image/'))?.getAsFile() ?? null;
}

/** The module's record type; the pill shows only where it is installed. */
export const FEEDBACK_TYPE = 'feedback_item';

export function FeedbackHost({ me, children }: { me: Me | undefined; children: React.ReactNode }) {
  const types = useAllEntityTypes();
  const available = Boolean(me && types.data?.some((t) => t.code === FEEDBACK_TYPE));
  const [form, setForm] = React.useState<{ kind: FeedbackKind; recordKey: string | null; origin: PillOrigin | null } | null>(null);
  const contextOf = useContextOf(me);
  const value = React.useMemo<FeedbackValue>(
    () => ({
      available,
      open: (kind, panel) => setForm({ kind, recordKey: panel.recordKey ?? null, origin: panel.origin ?? null }),
      describe: (origin, recordKey) => {
        const ctx = contextOf(origin, recordKey);
        return { text: aboutLine(ctx), route: ctx.route, screen: ctx.screenId ?? null, panel: ctx.panelId ?? null };
      },
    }),
    [available, contextOf],
  );
  return (
    <FeedbackProvider value={value}>
      {children}
      {form ? <FeedbackForm kind={form.kind} recordKey={form.recordKey} origin={form.origin} contextOf={contextOf} onClose={() => setForm(null)} /> : null}
    </FeedbackProvider>
  );
}

/**
 * R-16 T-125: the context a report records from what the pill read when it opened — the screen is the page's own id, else
 * the module contract's menu entry for that address (GET /api/apps: the platform's entries and Admin groups, every app's menus).
 */
function useContextOf(me: Me | undefined) {
  const { app } = useExperience();
  const version = useServingVersion();
  const apps = useApps(Boolean(me));
  const sha = version.data?.sha ?? null;
  const roles = me?.roles.map((r) => r.role).join(',') ?? '';
  return React.useCallback(
    (origin: PillOrigin, recordKey: string | null) => {
      const p = apps.data?.platform;
      const items = [...(p?.entries ?? []), ...(p?.adminGroups ?? []).flatMap((g) => g.items), ...(apps.data?.apps ?? []).flatMap((a) => a.menuBar.flatMap((m) => m.items))];
      const screenId = origin.screenId ?? screenIdFor(origin.pathname, origin.search, items);
      return feedbackContext({ pathname: origin.pathname, search: origin.search, app, recordKey, sha, roles: roles ? roles.split(',') : [], screenId, panelId: origin.panelId });
    },
    [apps.data, app, sha, roles],
  );
}

function FeedbackForm({ kind, recordKey, origin, contextOf, onClose }: { kind: FeedbackKind; recordKey: string | null; origin: PillOrigin | null; contextOf: ReturnType<typeof useContextOf>; onClose: () => void }) {
  const { pathname, search } = useLocation();
  // the origin read when the menu opened; a form opened without one (no pill) reads the address now
  const [from] = React.useState<PillOrigin>(() => origin ?? { pathname, search, screenId: null, panelId: null });
  const [title, setTitle] = React.useState('');
  const [text, setText] = React.useState('');
  const [image, setImage] = React.useState<File | null>(null);
  // The file input is uncontrolled; a new key on removal or a paste clears its selection so the same file can be picked again.
  const [pickKey, setPickKey] = React.useState(0);
  const replaceImage = (next: File | null) => {
    setImage(next);
    setPickKey((k) => k + 1);
  };
  const [note, setNote] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const preview = React.useMemo(() => (image ? URL.createObjectURL(image) : null), [image]);
  React.useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);
  /** T-182: Ctrl+V of an image takes the same path as the file input; a paste with no image stays a text paste. */
  const onPaste = (e: React.ClipboardEvent) => {
    const file = pastedScreenshot(Array.from(e.clipboardData.items), e.clipboardData.getData(PLAIN_TEXT));
    if (!file) return;
    // never replace a chosen screenshot silently: say so, and leave the paste alone
    if (image) return setNote(ALREADY_ATTACHED);
    setNote(null);
    replaceImage(file);
  };
  const ctx = contextOf(from, recordKey);
  const label = FEEDBACK_KINDS.find((k) => k.kind === kind)?.label ?? 'Feedback';
  const send = async () => {
    const built = feedbackBody(kind, { title, text, image }, ctx);
    if (!built.ok) return setError(built.error);
    setSending(true);
    try {
      const r = await apiUpload<{ key?: string; record?: { key?: string } }>('/feedback', built.form, { correlationId: newCorrelationId() });
      toast.success(`Thank you — ${r.key ?? r.record?.key ?? 'your report'} is with IT.`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <Modal
      open
      size="medium"
      title={label}
      onClose={onClose}
      dirty={Boolean(title.trim() || text.trim() || image)}
      data-testid="feedback-modal"
      footerWhy={error ?? undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={sending} onClick={() => void send()} data-testid="feedback-send">
            Send to IT
          </Button>
        </>
      }
    >
      <div onPaste={onPaste}>
      <FieldColumn>
        <Field label="What happened, or what you need" htmlFor="feedback-text">
          <Textarea id="feedback-text" data-testid="feedback-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
        </Field>
        <Field label="A few words for the title (optional)" htmlFor="feedback-title">
          <Input id="feedback-title" data-testid="feedback-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="A screenshot (optional)" htmlFor="feedback-image" hint="Only if you choose one. Nothing is captured without you.">
          <Input key={pickKey} id="feedback-image" data-testid="feedback-image" type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] ?? null)} />
        </Field>
        {note ? (
          <p className="k-meta" role="status" data-testid="feedback-paste-note">
            {note}
          </p>
        ) : null}
        {preview && image ? (
          <div className="flex items-center gap-2" data-testid="feedback-preview">
            <img src={preview} alt={`Pasted screenshot ${image.name}`} className="max-h-24 rounded" />
            <Button variant="quiet" onClick={() => { replaceImage(null); setNote(null); }} data-testid="feedback-image-remove">
              Remove screenshot
            </Button>
          </div>
        ) : null}
        <section aria-label="Sent with your report" data-testid="feedback-context">
          <p className="eyebrow mb-1">Sent with your report</p>
          <dl className="k-kv">
            {contextRows(ctx).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </section>
      </FieldColumn>
      </div>
    </Modal>
  );
}
